import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { IconColor } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useId } from "../lib/uid.ts";
import { ICON_BG } from "./iconColors.ts";
import { CloseIcon, ExpandIcon } from "./icons.tsx";
import { type MenuItem, MenuPopover } from "./Menu.tsx";
import { placePopover } from "./popover.ts";
import { FOCUS } from "./styles.ts";

/** A pill's look: set, tinted (an entity's, or blue); not, plain. In the iOS app (`ios:`), filled, no frame, a
 *  little taller; in the Android app (`android:`), Material's filter chip (framed until set, then tonal). */
const pillLook = (set: boolean, tint?: IconColor | null) =>
  `${
    set
      ? tint
        ? `${ICON_BG[tint]} border-transparent text-zinc-900`
        : "border-blue-200 bg-blue-50 text-blue-900 ios:border-transparent android:border-transparent android:bg-blue-100"
      : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 ios:border-transparent ios:bg-zinc-950/6 android:border-zinc-500 android:bg-transparent"
  } ios:min-h-8 android:min-h-8 android:rounded-lg`;

/** A filter that's on or off (archived ones only) as a pill: pressed, it's set, looking as a set pill does. */
export function TogglePill(p: { icon: ComponentChildren; name: string; pressed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={p.pressed}
      class={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm ${pillLook(p.pressed)} ${p.pressed ? "font-medium" : ""} ${FOCUS}`}
      onClick={p.onToggle}
    >
      {/* Its icon on or off: there's no chevron saying it's a filter. */}
      <span class="flex size-4 shrink-0 items-center justify-center">{p.icon}</span>
      {p.name}
    </button>
  );
}

/**
 * A list's filter as a pill: its name and a chevron, and once set its icon and what it's set to (tinted; an × takes it off). Clicked, a
 * small panel under it sets it (`children`, given a way to close it), on the Popover API (light dismiss, Esc). `open`:
 * open as it appears (a filter just added from Filters, nothing set yet). A filter of choices gives `items`
 * instead: a menu, as the app's others.
 */
export function FilterPill(p: {
  icon: ComponentChildren;
  name: string;
  /** What it's set to (none: not set). */
  value?: string | null | undefined;
  /** Its tint when set (an entity's): blue without one. */
  tint?: IconColor | null | undefined;
  onClear?: (() => void) | undefined;
  open?: boolean;
  /** Its choices, as a menu (the menus' look and keys): instead of a panel. */
  items?: readonly MenuItem[];
  /** Shown as its name (another entity's link, drawn): its icon left out, the plain name for screen readers. */
  label?: ComponentChildren;
  /** Its name shown along with its value (a value that doesn't say what it's of alone: an item, a condition's). */
  named?: boolean;
  /** A panel's width for a search or a form: otherwise as wide as what's in it. */
  wide?: boolean;
  /** Room around a form of several fields (a condition); a search, as a menu, has a menu's. */
  form?: boolean;
  children?: (close: () => void) => ComponentChildren;
}) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pill = useRef<HTMLSpanElement>(null);
  const set = p.value !== null && p.value !== undefined;
  // Its panel's content only while it's open (each pill's fields aren't all in the page at once).
  const [open, setOpen] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only as it first appears.
  useEffect(() => {
    if (p.open && !p.items) {
      panel.current?.showPopover();
    }
  }, []);
  const look = pillLook(set, p.tint);
  const close = () => panel.current?.hidePopover();
  return (
    <span
      ref={pill}
      data-pop-source=""
      class={`inline-flex max-w-full items-center rounded-full border text-sm ${look}`}
    >
      <button
        ref={trigger}
        type="button"
        popovertarget={id}
        aria-haspopup={p.items ? "menu" : "dialog"}
        class={`flex min-w-0 items-center gap-1.5 rounded-full py-1 ${set && p.onClear ? "pr-1 pl-2.5" : "px-2.5"} ${FOCUS}`}
      >
        {/* Its icon once set, saying what it's set to (an entity's, issue levels); unset, its name says it. */}
        {set && !p.label ? (
          <span class="flex h-4 min-w-4 shrink-0 items-center justify-center gap-0.5">{p.icon}</span>
        ) : null}
        {p.label ? (
          <>
            <span class="sr-only">{set ? `${t("items.filter.named", { name: p.name })} ` : p.name}</span>
            <span aria-hidden="true" class="flex min-w-0 items-center">
              {p.label}
            </span>
          </>
        ) : null}
        <span class="truncate">
          {set ? (
            <>
              {/* Set, its value says it alone (the name's for screen readers), unless it doesn't say what it's of. */}
              {p.label ? null : (
                <span class={p.named ? "text-zinc-600" : "sr-only"}>{t("items.filter.named", { name: p.name })} </span>
              )}
              <span class="font-medium">{p.value}</span>
            </>
          ) : p.label ? null : (
            p.name
          )}
        </span>
        {/* It opens a menu or panel (set, its × is there instead). */}
        {set && p.onClear ? null : <ExpandIcon size={14} class="shrink-0 opacity-60" />}
      </button>
      {set && p.onClear ? (
        <button
          type="button"
          class={`mr-1 flex size-5 shrink-0 items-center justify-center rounded-full opacity-70 hover:bg-zinc-950/10 hover:opacity-100 ${FOCUS}`}
          aria-label={t("items.removeFilter", { filter: `${p.name}: ${p.value}` })}
          onClick={p.onClear}
        >
          <CloseIcon size={14} />
        </button>
      ) : null}
      {p.items ? (
        <MenuPopover
          id={id}
          trigger={trigger}
          source={pill}
          items={p.items}
          aria-label={p.name}
          open={p.open ?? false}
        />
      ) : (
        <div
          ref={panel}
          id={id}
          popover="auto"
          // In a native app, on its own glass (lib/nativePopover.ts).
          data-native-pop=""
          role="dialog"
          aria-label={p.name}
          class={`fixed m-0 ${p.wide ? "w-80" : "w-max min-w-48"} ${p.form ? "p-4" : "p-2"} max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain rounded-md border border-zinc-200 bg-white text-sm text-zinc-800 shadow-lg`}
          onToggle={(e) => {
            const state = (e as unknown as ToggleEvent).newState;
            setOpen(state === "open");
            if (state === "open" && panel.current && trigger.current) {
              placePopover(panel.current, trigger.current);
            }
          }}
        >
          {open ? p.children?.(close) : null}
        </div>
      )}
    </span>
  );
}
